import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import { getAPICall, postAPICall } from "lib/callApi";
import { ASSIGN_KPI_TEMPLATE, FETCH_ALL_KPI_TEMPLATES } from "lib/constants";
import { useEffect, useState } from "react";
import KPITile from "./KPITileForHome";
// import EditKPITable from "./EditKPITable";

function KpiTemplateModalAssign({
  isMobileDisplay,
  handleClose,
  dontShowAgain,
  setDontShowAgain,
  showNotification,
  user,
  refetchKpiEntries,
  selectedTemplateId,
  setSelectedTemplateId,
  setCopying,
  searchFilter,
  setSearchFilter,
  checkboxFilters,
  setCheckboxFilters,
}) {
  const [templateData, setTemplateData] = useState([]);
  const [filteredTemplateData, setFilteredTemplateData] = useState([]);
  const [isRequestApiLoading, setIsRequestApiLoading] = useState(false);

  const handleCheckboxFiltersChanged = (event) => {
    const newFilters = { ...checkboxFilters };
    newFilters[event.target.value] = event.target.checked;
    setCheckboxFilters(newFilters);
  };

  const handleCloseAction = () => {
    handleClose();
  };

  const handleMakeCopyButton = () => {
    setCopying(true);
  };

  const handleAssign = async () => {
    setIsRequestApiLoading(true);
    try {
      const response = await postAPICall(ASSIGN_KPI_TEMPLATE, true, {
        templateId: selectedTemplateId,
        userId: user.id,
      });
      if (response?.data?.success) {
        showNotification("success", "Template assigned successfully!");
        refetchKpiEntries();
        handleClose(dontShowAgain);
      } else {
        showNotification(
          "error",
          response?.response?.data?.message || "Could not assign template, please try again!"
        );
      }
    } catch (error) {
      showNotification("error", "Could not assign template, please try again!");
    } finally {
      setIsRequestApiLoading(false);
    }
  };

  const getTemplates = async () => {
    try {
      const res = await getAPICall(FETCH_ALL_KPI_TEMPLATES, true);
      if (res?.data?.success) {
        setTemplateData(res?.data?.data?.templates || []);
      } else if (res?.response?.status !== 403) {
        showNotification("success", res?.response?.data?.message || res.message);
      }
    } catch (error) {
      showNotification(
        "success",
        error?.data?.message || error.response.data.message || error.message
      );
    }
  };

  useEffect(() => {
    getTemplates();
  }, []);

  useEffect(() => {
    const filteredTemplates = templateData.filter((template) => {
      const nameMatch = template.name.toLowerCase().includes(searchFilter.toLowerCase());
      const userMatch = template.Users.some((u) =>
        u.name.toLowerCase().includes(searchFilter.toLowerCase())
      );

      return nameMatch || userMatch;
    });
    const filteredCheckboxTemplates = filteredTemplates.filter((template) => {
      if (!checkboxFilters.showActive && template.Users.length > 0) {
        return false;
      }
      if (!checkboxFilters.showUnassigned && template.Users.length === 0) {
        return false;
      }
      if (!checkboxFilters.showAlysio && template.status === "alysio_template") {
        return false;
      }
      return true;
    });
    setFilteredTemplateData(filteredCheckboxTemplates);

    // Deselect if filtered out
    if (!filteredCheckboxTemplates.some((t) => t.id === selectedTemplateId)) {
      setSelectedTemplateId(null);
    }
  }, [templateData, searchFilter, checkboxFilters]);

  return (
    <>
      <Box>
        <Box sx={{ mb: "1.5rem" }}>
          <Typography variant="h4" sx={{ mb: "0.5rem" }}>
            Select KPI Template
          </Typography>
        </Box>
        <Box display="flex" flexDirection={isMobileDisplay ? "column" : "row"}>
          <MDInput
            label="Search Template or User"
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            sx={{ mr: "1rem", width: "200px" }}
          />
          <FormGroup
            aria-label="position"
            sx={{ flexDirection: isMobileDisplay ? "column" : "row" }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  value="showActive"
                  checked={checkboxFilters.showActive}
                  onChange={handleCheckboxFiltersChanged}
                />
              }
              label={<Typography sx={{ fontSize: "0.85rem" }}>Show Active</Typography>}
              sx={{ display: "flex" }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  value="showUnassigned"
                  checked={checkboxFilters.showUnassigned}
                  onChange={handleCheckboxFiltersChanged}
                />
              }
              label={<Typography sx={{ fontSize: "0.85rem" }}>Show Unassigned</Typography>}
              sx={{ display: "flex" }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  value="showAlysio"
                  checked={checkboxFilters.showAlysio}
                  onChange={handleCheckboxFiltersChanged}
                />
              }
              label={<Typography sx={{ fontSize: "0.85rem" }}>Show Alysio Templates</Typography>}
              sx={{ display: "flex" }}
            />
          </FormGroup>
        </Box>
      </Box>
      <Divider />
      <Box>
        <Grid
          container
          xs={12}
          spacing={isMobileDisplay ? 1 : 2}
          direction="row"
          justifyContent="flex-start"
          alignItems="flex-start"
          sx={{ ml: "0rem", maxHeight: "500px", overflow: "auto", p: "0 1rem 0.5rem 0" }}
        >
          {filteredTemplateData.map((template) => (
            <KPITile
              template={template}
              selectedTemplateId={selectedTemplateId}
              setSelectedTemplateId={setSelectedTemplateId}
            />
          ))}
        </Grid>
      </Box>
      <Divider />
      <Box>
        <Stack direction={isMobileDisplay ? "column" : "row"} justifyContent="space-between">
          <Box mt={1}>
            <MDButton
              variant="outlined"
              color="black"
              disabled={isRequestApiLoading || !selectedTemplateId}
              onClick={handleMakeCopyButton}
            >
              Make a Copy
            </MDButton>
          </Box>
          <MDBox
            mt={1}
            sx={{
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={dontShowAgain}
                  onChange={(e) => setDontShowAgain(e.target.checked)}
                />
              }
              label={<Typography sx={{ fontSize: "0.85rem" }}>Don&rsquo;t show again</Typography>}
              sx={{ display: "flex" }}
            />
            <MDButton onClick={handleCloseAction} color="gray80" style={{ marginRight: "10px" }}>
              Close
            </MDButton>
            <MDButton
              color="primaryRed"
              onClick={handleAssign}
              disabled={isRequestApiLoading || !selectedTemplateId}
            >
              {isRequestApiLoading ? "Loading..." : "Assign"}
            </MDButton>
          </MDBox>
        </Stack>
      </Box>
    </>
  );
}

export default KpiTemplateModalAssign;
